import type { ParsedQuery } from 'query-string';
import qs from 'query-string';
import globalWindow from '@shared/core/globals';
import { useMemo } from 'react';
// Direct path on purpose: `@react-router` (the wrapper) imports the feature-flags provider, which
// imports this module, so importing the wrapper here would be a cycle that crashes the SSR bundle.
import { selectLocationSearch, useLocationSelector } from '@shared/core/router.helper/useLocationSelector';
import type { Location as HistoryLocation } from 'history';

type PartialLocation = Pick<Location, 'pathname' | 'search'>;
type FilterQueryParamsMode = 'exclude' | 'include';
export type FilterQueryParamsOptions = Readonly<{
  mode?: FilterQueryParamsMode;
}>;
type QueryStringHelpers = Readonly<{
  getParamNames: () => string[];
  hasValue: (key: string) => boolean;
  getValue: (key: string) => string | ReadonlyArray<string> | undefined;
  getValueArray: (key: string) => ReadonlyArray<string> | undefined;
  getValueString: (keys: string | string[]) => string | undefined;
  getValueInteger: (key: string) => number | undefined;
  buildRedirectPath: (path: string, maybeParams?: QueryStringMap | null) => string;
  buildPartialLocation: (pathname: string, maybeParams?: QueryStringMap | null) => PartialLocation;
  filterQueryParamsFromLocation: (
    location: Maybe<string | Pick<Location, 'pathname' | 'search' | 'hash'>>,
    params?: ReadonlyArray<string>,
    options?: FilterQueryParamsOptions,
  ) => string;
}>;

export type QueryStringMap = { [key: string]: string | ReadonlyArray<string> | null };
type LocationLike = string | Pick<Location, 'pathname' | 'search' | 'hash'>;

const mapAsLowercaseKeys = (parsedQueryString: QueryStringMap): QueryStringMap => {
  const map: QueryStringMap = {};
  Object.keys(parsedQueryString).forEach(key => {
    const value = parsedQueryString[key];
    map[key] = value;

    if (map[key.toLowerCase()] === undefined) {
      map[key.toLowerCase()] = value;
    }
  });
  return map;
};

const getParamNames = (parsedQueryString: QueryStringMap): string[] => {
  return Object.keys(parsedQueryString);
};

const hasValueGetterForMap =
  (map: QueryStringMap) =>
  (key: string): boolean => {
    const retVal = map[key.toLowerCase()];
    return retVal !== null && retVal !== undefined;
  };

const valueGetterForMap =
  (map: QueryStringMap) =>
  (key: string): string | ReadonlyArray<string> | undefined => {
    const retVal = map[key.toLowerCase()];
    return retVal as string;
  };

const valueArrayGetterForMap =
  (map: QueryStringMap) =>
  (key: string): ReadonlyArray<string> | undefined => {
    const retVal = map[key.toLowerCase()];
    return typeof retVal === 'string' ? [retVal] : retVal || undefined;
  };

const valueStringGetterForMap =
  (map: QueryStringMap) =>
  (keys: string | string[]): string | undefined => {
    const searchKeys = typeof keys === 'string' ? [keys] : keys;
    const key = searchKeys.map(searchKey => searchKey.toLowerCase()).find(key => key in map);
    const retVal = key && map[key];
    return typeof retVal === 'string' ? retVal : retVal ? retVal?.join(',') : undefined;
  };

const valueIntegerGetterForMap =
  (map: QueryStringMap) =>
  (key: string): number | undefined => {
    const retVal = map[key.toLowerCase()];
    if (typeof retVal !== 'string') {
      return;
    }
    const integer = parseInt(retVal);
    if (Number.isNaN(integer)) {
      return;
    }
    return integer;
  };

const stringifyPreservingOrder = (query: ParsedQuery<string>) => qs.stringify(query, { sort: false });
const buildSearchPrefix = (query: ParsedQuery<string>) => {
  const searchString = stringifyPreservingOrder(query);
  return searchString ? `?${searchString}` : '';
};

const separatePathFromParams = (path: string, maybeParams?: QueryStringMap | null): { pathname: string; params: QueryStringMap; fragmentIdentifier?: string | null } => {
  const { url, query, fragmentIdentifier } = qs.parseUrl(path, { parseFragmentIdentifier: true, sort: false });
  const params = Object.assign({}, maybeParams, query);
  return {
    pathname: url,
    params,
    fragmentIdentifier,
  };
};

/**
 * If you do not specify `maybeParams`, existing querystring parameters will be propagated automatically.
 * If you want to ignore / erase existing querystring parameters, pass an empty params object.
 */
const redirectPathBuilderForMap = (map: QueryStringMap) => (path: string, maybeParams?: QueryStringMap | null) => {
  const { pathname, params } = separatePathFromParams(path, maybeParams ?? map);
  const queryString = qs.stringify(params);
  const redirectPath = queryString ? `${pathname}?${queryString}` : pathname;
  return redirectPath;
};

/**
 * For use with 'react-router' v5's [<Redirect /> Component](https://v5.reactrouter.com/web/api/Redirect).
 * If you do not specify `maybeParams`, existing querystring parameters will be propagated automatically.
 * If you want to ignore / erase existing querystring parameters, pass an empty params object.
 */
const partialLocationBuilderForMap =
  (map: QueryStringMap) =>
  (path: string, maybeParams?: QueryStringMap | null): PartialLocation => {
    const { pathname, params } = separatePathFromParams(path, maybeParams ?? map);
    return {
      pathname,
      search: qs.stringify(params),
    };
  };

const normalizeFilterKeys = (paramsToFilter?: ReadonlyArray<string>): string[] => {
  if (!paramsToFilter) {
    return [];
  }
  return paramsToFilter.map(param => param.trim().toLowerCase()).filter(param => param.length > 0);
};

const resolveLocationParts = (location: Maybe<LocationLike>, fallbackParsed: ParsedQuery<string>) => {
  if (typeof location === 'string') {
    const { pathname, params, fragmentIdentifier } = separatePathFromParams(location);
    return {
      pathname,
      hash: fragmentIdentifier ? `#${fragmentIdentifier}` : '',
      parsed: params as ParsedQuery<string>,
    };
  }

  const hasExplicitSearch = typeof location?.search === 'string';
  return {
    pathname: location?.pathname ?? '',
    hash: location?.hash ?? '',
    parsed: hasExplicitSearch ? safelyParseSearch(location!.search as string) : fallbackParsed,
  };
};

const filterQueryParamsFromLocationBuilder =
  (defaultParsed: ParsedQuery<string>) =>
  (location: Maybe<LocationLike>, paramsToFilter?: ReadonlyArray<string>, options?: FilterQueryParamsOptions): string => {
    const { pathname, hash, parsed } = resolveLocationParts(location, defaultParsed);
    const filterKeys = normalizeFilterKeys(paramsToFilter);

    if (filterKeys.length === 0) {
      return `${pathname}${buildSearchPrefix(parsed)}${hash}`;
    }

    const filterMode: FilterQueryParamsMode = options?.mode ?? 'include';
    const filterSet = new Set(filterKeys);
    const shouldInclude = filterMode === 'include';

    const filteredParams = Object.keys(parsed).reduce((acc, key) => {
      const matches = filterSet.has(key.toLowerCase());
      if ((shouldInclude && matches) || (!shouldInclude && !matches)) {
        acc[key] = parsed[key];
      }
      return acc;
    }, {} as ParsedQuery<string>);

    return `${pathname}${buildSearchPrefix(filteredParams)}${hash}`;
  };

// to normalize behavior across browser (window.location) and SSR (StaticContext)
const PARSED_BLANK_QUERY_STRING = qs.parse('', { sort: false });
type ParsedOrRawQueryString = string | ParsedQuery<string>;
const safelyParseSearch = (search: ParsedOrRawQueryString | undefined): ParsedQuery<string> => {
  if (typeof search === 'undefined') {
    return PARSED_BLANK_QUERY_STRING;
  }
  if (typeof search !== 'string') {
    return search;
  }
  // return parsed query string with params in the same order the params appeared
  return qs.parse(search, { sort: false });
};

export const deriveQueryStringHelpersFromLocation = (location: Maybe<Location | HistoryLocation>): QueryStringHelpers => {
  return deriveQueryStringHelpersFromSearchString(location?.search);
};

export const deriveQueryStringHelpersFromSearchString = (search: string = ''): QueryStringHelpers => {
  const parsedQueryString = safelyParseSearch(search);
  const mapIncludingLowerCaseKeys = mapAsLowercaseKeys(parsedQueryString);

  return {
    /**
     * Fetches the value of a key in a case insensitive manner.
     * Converts any array values into comma separated strings.
     */
    getParamNames: () => getParamNames(parsedQueryString),
    hasValue: hasValueGetterForMap(mapIncludingLowerCaseKeys),
    getValue: valueGetterForMap(mapIncludingLowerCaseKeys),
    getValueArray: valueArrayGetterForMap(mapIncludingLowerCaseKeys),
    getValueString: valueStringGetterForMap(mapIncludingLowerCaseKeys),
    getValueInteger: valueIntegerGetterForMap(mapIncludingLowerCaseKeys),
    buildRedirectPath: redirectPathBuilderForMap(parsedQueryString),
    buildPartialLocation: partialLocationBuilderForMap(parsedQueryString),
    filterQueryParamsFromLocation: filterQueryParamsFromLocationBuilder(parsedQueryString),
  };
};

const selectSearchAndHash = (location: HistoryLocation): readonly [string, string] => [location.search, location.hash];
const isSameSearchAndHash = (a: readonly [string, string], b: readonly [string, string]) => a[0] === b[0] && a[1] === b[1];

/**
 * The helpers only ever read `location.search`, so they subscribe to that string rather than
 * to the router's location object. Every `useIsFeatureEnabled` call goes through here (via
 * `useEventFeatureFlagsContext`), which made each of those hundreds of call sites a react-router
 * subscriber that re-rendered — through any memo boundary — on every scroll-driven URL replace.
 */
export function useQueryParamHelper() {
  const search = useLocationSelector(selectLocationSearch);

  return useMemo(() => deriveQueryStringHelpersFromSearchString(search), [search]);
}

export function useNestedQueryParamHelper(parentQueryParam: string) {
  const search = useLocationSelector(selectLocationSearch);

  return useMemo(() => {
    const maybeNestedParam = deriveQueryStringHelpersFromSearchString(search).getValueString(parentQueryParam);

    return deriveQueryStringHelpersFromSearchString(maybeNestedParam);
  }, [parentQueryParam, search]);
}

export function useHashParamHelper(): Pick<QueryStringHelpers, 'getValueString'> {
  // One subscription for both fields: two calls meant two `history.listen` registrations and two
  // selector runs per render for values that come off the same location.
  const [search, hashString] = useLocationSelector(selectSearchAndHash, isSameSearchAndHash);

  return useMemo(() => {
    const hash = deriveQueryStringHelpersFromSearchString(hashString);
    const query = deriveQueryStringHelpersFromSearchString(search);

    return {
      getValueString: keys => hash.getValueString(keys) ?? query.getValueString(keys),
    };
  }, [hashString, search]);
}

export { qs };

// for client diagnostics and useTranslation, it's important to use
// global window's location and not useLocation's location from react-router
const singleton = deriveQueryStringHelpersFromSearchString(globalWindow.location?.search);
export default singleton;
